import React from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { createLogger } from "@/lib/logger";

const log = createLogger("error-boundary");

/**
 * Application error boundary — the frontend counterpart to the backend's
 * central error handling. Catches render errors and shows a recoverable state.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    log.error("Render error", { message: error?.message, info });
  }

  handleReset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="grid min-h-[60vh] place-items-center p-6"
        data-testid="error-boundary"
      >
        <div className="max-w-md rounded-xl border border-border bg-card p-8 text-center shadow-card">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h2 className="font-brand text-xl font-semibold text-foreground">
            Something went wrong
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            An unexpected error occurred while rendering this view.
          </p>
          <Button className="mt-6" onClick={this.handleReset} data-testid="error-retry">
            Try again
          </Button>
        </div>
      </div>
    );
  }
}
